// What is waiting between this station and the next, per flowline.
//
// This replaces the old priority badge (`lib/priority.ts`), which ranked each
// line by how badly the station downstream needed feeding and wrote that as an
// instruction — "FL - Plumbing starved", "backed up", a red banner above the
// list. On the floor it was read as something else ("are we out of
// sequence?"), and the 28 Oct 2026 systems meeting asked for it to go. What
// replaced it is a count and nothing more: the operator can see the pile and
// decide what it means.
//
// The count is Odoo's `outbound_waiting` on this station's own state row: pods
// finished HERE whose next operation has not started yet. It is a fact about
// the pile, not a judgement, so it carries no colour.
//
// Where a buffer is configured out of this station on this line
// (`mrp.flowline.buffer`), the buffer is shown instead — its fill and its own
// state, which is Odoo's judgement against levels someone set, and so the one
// place a colour is earned. It replaces the count rather than sitting beside
// it: both measure the same pods between the same two operations, and two
// nearly-equal numbers side by side would only invite the question of which is
// right. Production has no flowline-process module yet, so there the count is
// all anyone sees.

import type { FlowlineBufferVM } from '@/api/types';
import type { Translations } from '@/i18n/strings';
import { type BufferEdge, type FlowlineTopology, successorsOf } from './topology';

export interface OutboundVM {
  /** Finished here, next operation not started — Odoo's `outbound_waiting`. */
  waiting: number;
  /**
   * The station those pods wait for, when the route names exactly one. Where
   * it fans out the count covers every branch, so no single name is true.
   */
  nextName: string | null;
  /** Buffers configured out of this station on this line, in route order. */
  buffers: OutboundBuffer[];
}

export interface OutboundBuffer extends BufferEdge {
  /** The station the buffer feeds, for telling two apart where the route fans out. */
  toName: string | null;
}

/**
 * The pile behind this station on one flowline, or null where there is nothing
 * to say: the end of the line, or a station whose route no source can state
 * and that has nothing waiting on it either.
 */
export function outboundFor(
  topology: FlowlineTopology,
  workcentreId: number,
  flowlineId: number,
  outboundWaiting: number,
): OutboundVM | null {
  const next = successorsOf(topology, workcentreId, flowlineId);
  const buffers = topology.buffers
    .filter((b) => b.flowlineId === flowlineId && b.fromWorkcentreId === workcentreId)
    .map((b) => ({ ...b, toName: next.find((n) => n.workcentreId === b.toWorkcentreId)?.workcentreName || null }));
  if (next.length === 0 && buffers.length === 0 && outboundWaiting === 0) return null;
  return {
    waiting: outboundWaiting,
    nextName: next.length === 1 ? next[0]!.workcentreName || null : null,
    buffers,
  };
}

export interface OutboundChip {
  label: string;
  tone: 'neutral' | 'danger' | 'warn' | 'accent' | 'pos';
}

/**
 * The chips a line card wears: one per outbound buffer where any are
 * configured, otherwise the single uncoloured count.
 */
export function outboundChips(vm: OutboundVM, t: Translations): OutboundChip[] {
  if (vm.buffers.length > 0) {
    const named = vm.buffers.length > 1;
    return vm.buffers.map((b) => ({
      label: `${named && b.toName ? `→ ${b.toName} ` : ''}${b.filled} ${t.inBufferT} · ${bufferStateWord(b.state, t)}`,
      tone: bufferChipTone(b.state),
    }));
  }
  return [
    {
      label: vm.nextName ? `${vm.waiting} ${t.waitingForT} ${vm.nextName}` : `${vm.waiting} ${t.waitingDownstreamT}`,
      tone: 'neutral',
    },
  ];
}

/** The same words the leader's Buffers view uses for these states. */
function bufferStateWord(state: FlowlineBufferVM['state'], t: Translations): string {
  switch (state) {
    case 'critical':
      return t.criticalT;
    case 'replenish':
      return t.lowLevelT;
    case 'over_production':
      return t.overT;
    default:
      return t.inRangeT;
  }
}

/** A buffer's own state as a chip tone. Odoo decides the state; this only colours it. */
export function bufferChipTone(state: FlowlineBufferVM['state']): 'danger' | 'warn' | 'accent' | 'pos' {
  switch (state) {
    case 'critical':
      return 'danger';
    case 'replenish':
      return 'warn';
    case 'over_production':
      return 'accent';
    default:
      return 'pos';
  }
}
